// ══════════════════════════════════════════════════════════════════════════
//  🔡 SCB — un texto que SE ESCRIBE en vez de aparecer.
// ══════════════════════════════════════════════════════════════════════════
//
// Una sola función. Le das un nodo y el texto que tiene que decir, y en lugar
// de cambiar de golpe pasa por ruido y se asienta de izquierda a derecha.
//
// Lo hace `scrambleText` de `animejs/text`, que NO es un invento de la casa:
// existe desde la 4.4.1 (medido: la 4.3.x no trae el módulo `text/scramble`)
// y su firma está en `node_modules/animejs/dist/modules/text/scramble.d.ts`.
// Devuelve un VALOR-FUNCIÓN para un tween, así que se pasa como valor de una
// propiedad de `animate()`, no se llama a mano.
//
// ⚠️ LA PROPIEDAD ES `textContent`, Y ESO TIENE MOTIVO. Para anime un nodo del
// DOM con una propiedad que no es CSS ni atributo SVG es un tween de tipo
// OBJECT (`getTweenType`: cae en `prop in target`), y un tween OBJECT escribe
// el valor DIRECTAMENTE en el nodo — `target.textContent = <cadena>`. Con
// `innerHTML` funcionaría igual y además interpretaría marcado: el texto de
// una miga viene del servidor, así que se escribe donde no puede inyectarse.
//
// ⚠️ AQUÍ NO SE TOCA `engine.timeUnit`, Y ES LA REGLA QUE HACE QUE ESTE
// FICHERO PUEDA SER EL MISMO EN LOS TRES REPOS. Ese setter escribe
// `globals.timeScale` (`animejs/dist/modules/engine/engine.js`), o sea que
// gobierna TODAS las animaciones del bundle: un módulo compartido que lo
// cambiara rompería al resto. La duración ENTRA POR PARÁMETRO, en la unidad
// que ya use quien llama — CRAFT y PULSAR en SEGUNDOS (`timeUnit = 's'`),
// NEXUS en MILISEGUNDOS. Los internos de `scrambleText` (el `interval` entre
// caracteres, el `settleDuration`) se derivan de `globals.timeScale`, así que
// se adaptan solos a las dos escalas sin que este fichero se entere.
//
// ⚠️ CON «REDUCIR MOVIMIENTO» NO HAY RUIDO: el texto final, directo y sin
// animación. Es la misma mitad del acuerdo que 🌌BGG.js y el fondo.
//
// ══════════════════════════════════════════════════════════════════════════
//  DÓNDE ESTÁ CABLEADO, Y DÓNDE NO — léelo antes de buscar al llamante
// ══════════════════════════════════════════════════════════════════════════
//
// Este fichero es IDÉNTICO en los tres repos (CRAFT, NEXUS, PULSAR) a
// propósito: así un arreglo se mueve de uno a otro copiándolo, sin traducirlo
// — la misma propiedad que paga la identidad de 🌌BGG.js.
//
//   · NEXUS  → CABLEADO. `🌊FLW.js` lo llama al navegar, sobre el <span> de
//              la miga (`.NAV_crumb [data-for=…]`, public/pug/layouts/panel.pug).
//   · CRAFT  → IMPORTADO Y SIN CABLEAR. No hay miga en este panel: la
//              navegación es de dos secciones y la marca el rail. El fichero
//              está aquí por identidad, no por uso.
//   · PULSAR → IMPORTADO Y SIN CABLEAR. Mismo motivo: el nav de UAC marca la
//              sección con `.act` sobre el enlace, y no hay migas.
//
// Que en dos repos no tenga llamante NO es un descuido: es el precio de que
// el fichero sea el mismo en los tres. Si algún día CRAFT o PULSAR estrenan
// una miga, se cablea ahí sin tocar una línea de aquí.

import { animate } from 'animejs'
import { scrambleText } from 'animejs/text'

/**
 * Escribe `texto` dentro de `el` con el scramble.
 *
 * @param {Element|null} el        el nodo cuyo textContent se escribe
 * @param {string|null}  texto     el destino; `null` = el que ya tiene
 * @param {number}       duracion  en la unidad del repo (s en CRAFT/PULSAR, ms en NEXUS)
 * @returns {object|null} la animación, o null si no hubo (sin nodo, o con
 *                        «reducir movimiento»: ahí el texto ya está puesto)
 */
export function escribirSCB(el, texto, duracion) {
  if (!el) return null

  const destino = texto ?? el.textContent ?? ''

  // El cinturón, y va ANTES de tocar nada: con «reducir movimiento» el texto
  // final se pone y se acabó. Sin animación que cancelar y sin estado que
  // limpiar — quien llame no tiene que preguntar nada.
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    el.textContent = destino
    return null
  }

  return animate(el, {
    textContent: scrambleText({
      text: destino,
      // Minúsculas: el ruido tiene que leerse como texto que aún no cuaja, no
      // como una matrícula. Con el juego por defecto (a-zA-Z0-9!%#_) entran
      // símbolos que hacen saltar la línea de base.
      chars: 'lowercase',
      // De izquierda a derecha, como se lee. `override: ''` arranca en blanco:
      // el texto CRECE en vez de sustituir carácter a carácter al anterior,
      // que es lo que hace que parezca escrito y no corrompido.
      from: 'left',
      override: '',
      // Un poco de desorden en el arranque y el final de cada carácter: sin
      // esto la ola de revelado es una línea recta y se ve el mecanismo.
      perturbation: .4,
      duration: duracion,
      // ⚠️ ESTO NO ES UN AJUSTE FINO: SIN ELLO NO SE ESCRIBE NADA. El
      // `settleDuration` por defecto son 300 ms ABSOLUTOS —cuánto pasa cada
      // carácter revolviéndose antes de cuajar—, y en una coreografía corta
      // resulta MÁS LARGO que la animación entera. Cuando eso pasa, la ola de
      // revelado no llega a avanzar: se ve ruido de principio a fin y luego un
      // salto al texto final. Medido pasando el modifier a mano, fotograma a
      // fotograma: con el defecto, a 167 de 180 ms seguía siendo «bblfzld»;
      // atado a la duración, a esa altura ya dice «backups» y por el camino ha
      // ido creciendo desde la izquierda. Como fracción, escala solo con la
      // duración que pase quien llame, y da igual la unidad del repo.
      settleDuration: duracion * .35,
    }),
    duration: duracion,
    ease: 'linear',
  })
}
